import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { useT } from '../i18n';
import { ArrowRight, Mail } from 'lucide-react';

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 tracking-tight">{title}</h2>
      <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-neutral-700">{children}</div>
    </section>
  );
}

export default function PrivacyPolicyPage() {
  const { lang } = useT();
  return (
    <div className="pt-28 pb-20">
      <SEO
        title={lang === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
        description="How Kitxens collects, uses and safeguards your personal information."
        path="/privacy"
      />
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-[#F77F11] text-xs tracking-[0.25em] font-semibold mb-3">LEGAL</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-neutral-900 tracking-[-0.02em]">Privacy Policy</h1>
          <p className="mt-3 text-sm text-neutral-500">Last updated: 2026</p>
        </motion.div>

        <Section title="Our Commitment">
          <p>
            In compliance with the Federal Law on Protection of Personal Data Held by Private Parties (LFPDPPP) in México, we share our Privacy Policy and personal data management practices, making the following commitments.
          </p>
        </Section>

        <Section title="Information We Collect">
          <p>
            We use Internet technologies to manage our website and email programs, but not to gather or store personal information beyond what is voluntarily provided through our forms.
          </p>
          <p>
            The data you provide us through printed or electronic forms — for registering as a volunteer, donor, beneficiary, customer or prospect — will be used solely to establish contact with you regarding your request, comment or participation in our work.
          </p>
        </Section>

        <Section title="How We Use Your Data">
          <p>
            The data you share with us will not be disseminated, distributed or commercialized. We use it only to respond to your inquiries, deliver the services you request, and improve our offering.
          </p>
          <p>
            Your request or comment may be included in statistical reports prepared for the monitoring of institutional progress. These reports are purely statistical and will not include any information that allows you to be identified individually.
          </p>
        </Section>

        <Section title="Your Rights">
          <p>
            If you wish to be removed from our database, please request it at
            <a href="https://www.kitxens.com" className="text-[#F77F11] underline ml-1">www.kitxens.com</a>
            or in writing to:
          </p>
          <p className="pl-4 border-l-2 border-orange-200">
            PUE MEXICO S.A.P.I. DE C.V.<br />
            Avenida Pdte. Masaryk 111, Piso 1, Polanco, 11560.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            For more information, write to{' '}
            <a href="mailto:info@kitxens.com" className="text-[#F77F11] underline">info@kitxens.com</a>{' '}
            or in writing to Avenida Pdte. Masaryk 111, Piso 1, Polanco, 11560.
          </p>
        </Section>

        <div className="mt-12 p-6 rounded-2xl bg-orange-50 border border-orange-100 flex items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-neutral-900">Questions about your data?</p>
            <p className="text-sm text-neutral-600">We’re here to help — reach out anytime.</p>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-1 bg-[#F77F11] hover:bg-[#d8651a] text-white px-5 h-11 rounded-full text-sm font-semibold whitespace-nowrap"
          >
            <Mail className="w-4 h-4" /> Contact us <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
